import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { AimingProvider } from 'contexts/aiming.context';
import { isBefore, parseISO } from 'date-fns';
import { IVerboseComponent } from 'interfaces/i-components';
import { IIgnoreAutoFireProp } from 'interfaces/i-firing';
import { ITrainingMsgExt } from 'interfaces/i-training';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { TrainingMode } from 'lib_ts/enums/machine.enums';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const CONTEXT_NAME = 'TrainingContext';

// detectionFailed will not auto-reset to false if there are too many fails in a row
export const MAX_CONSECUTIVE_FAILS = 5;

// how long before auto-dismissing the warning about missed detections
export const DETECTION_FAILED_DELAY_S = 5;

export interface ITrainingContext {
  mode: TrainingMode;

  lastUpdated: Date;

  // e.g. whether to show the callout re: failed detection and changing tracking devices
  warnFailure: boolean;

  failures: number;

  // for children to see what has arrived since the last reset
  msgs: ITrainingMsgExt[];

  finalMsg: ITrainingMsgExt | undefined;
  // e.g. when a new fire happens and previous msgs are no longer relevant
  readonly resetMsgs: () => void;
  readonly dismissFailedCallout: (resetFailureCount?: boolean) => void;

  pitchIndex: number;
  readonly setPitchIndex: Dispatch<SetStateAction<number>>;

  shots: IMachineShot[];
  readonly setShots: (v: IMachineShot[]) => void;

  // whether a live stream from the machine is on/off
  liveView: boolean;
  readonly setLiveView: Dispatch<SetStateAction<boolean>>;

  startDate: Date | undefined;
  readonly setStartDate: Dispatch<SetStateAction<Date | undefined>>;
}

const DEFAULT: ITrainingContext = {
  mode: TrainingMode.Quick,
  lastUpdated: new Date(),

  warnFailure: false,
  failures: 0,

  msgs: [],
  finalMsg: undefined,
  resetMsgs: () => console.error(`${CONTEXT_NAME}: not init`),

  dismissFailedCallout: () => console.error(`${CONTEXT_NAME}: not init`),

  pitchIndex: 0,
  setPitchIndex: () => console.error(`${CONTEXT_NAME}: not init`),

  shots: [],
  setShots: () => console.error(`${CONTEXT_NAME}: not init`),

  liveView: false,
  setLiveView: () => console.error(`${CONTEXT_NAME}: not init`),

  startDate: undefined,
  setStartDate: () => console.error(`${CONTEXT_NAME}: not init`),
};

export const TrainingContext = createContext(DEFAULT);

interface IProps extends IIgnoreAutoFireProp {
  // usually from user setting, but can be overridden (e.g. to manual mode during a home game)
  mode: TrainingMode | undefined;

  children: ReactNode;

  // if something has to happen after a training msg is received by the context, e.g. fire button
  // use this if the action exists in the parent to the provider (otherwise just react to changes in last updated or msgs)
  afterTrainingMsg?: (msg: ITrainingMsgExt) => void;
}

export const TrainingProvider: FC<IProps & IVerboseComponent> = (props) => {
  // for disregarding any training response msgs for fires from before the first fire of this context
  const [startDate, setStartDate] = useState<Date>();

  const [pitchIndex, setPitchIndex] = useState(DEFAULT.pitchIndex);

  // counts up whenever a failed detection occurs, resets to 0 whenever a successful detection occurs
  const [failures, setFailures] = useState(0);

  // separate from _failCount to allow dismissing the callout without resetting the counter
  const [warnFailure, setWarnFailure] = useState(DEFAULT.warnFailure);

  const hideWarningTimeout = useRef<NodeJS.Timeout>();

  const [trainingMsgs, setTrainingMsgs] = useState<ITrainingMsgExt[]>([]);

  const lastUpdated = useMemo(() => new Date(), [trainingMsgs]);

  const finalMsg = useMemo(
    () => trainingMsgs.find((m) => typeof m.success === 'boolean'),
    [trainingMsgs]
  );

  const [shots, setShots] = useState(DEFAULT.shots);
  const [liveView, setLiveView] = useState(DEFAULT.liveView);

  const state: ITrainingContext = {
    mode: props.mode ?? TrainingMode.Quick,
    lastUpdated: lastUpdated,
    warnFailure: warnFailure,
    failures: failures,
    msgs: trainingMsgs,
    finalMsg: finalMsg,
    resetMsgs: () => setTrainingMsgs([]),
    dismissFailedCallout: (resetFailureCount) => {
      setWarnFailure(false);
      if (resetFailureCount) {
        setFailures(0);
      }
    },

    pitchIndex: pitchIndex,
    setPitchIndex: setPitchIndex,

    shots: shots,
    setShots: setShots,

    liveView: liveView,
    setLiveView: setLiveView,

    startDate: startDate,
    setStartDate: setStartDate,
  };

  // listen for training msgs
  useEffect(() => {
    const callback = (event: CustomEvent) => {
      const data: ITrainingMsgExt = {
        ...event.detail,
        received: new Date(),
      };

      if (!startDate) {
        console.warn(
          'Ignoring training message; training context has no start date'
        );
        return;
      }

      if (isBefore(parseISO(data.fire._created), startDate)) {
        console.warn(
          `Ignoring training message; fire _created (${
            data.fire._created
          }) predates training start date (${startDate.toISOString()})`
        );
        // ignore this event as it came from a previous training session
        return;
      }

      setTrainingMsgs((prev) => [...prev, data]);

      switch (data.success) {
        case true: {
          setFailures(0);
          setWarnFailure(false);
          break;
        }

        case false: {
          setFailures((prev) => prev + 1);
          setWarnFailure(true);
          break;
        }

        case undefined:
        default: {
          // not a final msg
          break;
        }
      }

      props.afterTrainingMsg?.(data);
    };

    WebSocketHelper.on(WsMsgType.M2U_TrainingResponse, callback);

    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_TrainingResponse, callback);
    };
  }, [startDate]);

  useEffect(() => {
    if (!warnFailure) {
      // do nothing while warning isn't shown
      return;
    }

    clearTimeout(hideWarningTimeout.current);

    // auto-dismiss the warning after a timeout (if not at or above threshold)
    hideWarningTimeout.current = setTimeout(() => {
      if (failures >= MAX_CONSECUTIVE_FAILS) {
        return;
      }

      setWarnFailure(false);
    }, DETECTION_FAILED_DELAY_S * 1000);
  }, [failures, warnFailure]);

  return (
    <TrainingContext.Provider value={state}>
      <AimingProvider
        identifier="TrainingCxAimingCx"
        newerThan={startDate?.toISOString()}
        ignoreAfOnTr={props.ignoreAfOnTr}
        verbose={props.verbose}
        training
      >
        {props.children}
      </AimingProvider>
    </TrainingContext.Provider>
  );
};
